<script setup lang="ts">
import { XButton, XIcon, XBadge, XSkeletonLoader } from '@clicksign/excalibur';
import { useI18n } from '#i18n';
import { useToast } from 'vue-toastification';

import { useCurrentSignatureRequestKey } from '~~core';

import { fetchDocumentSummaries } from '../../services/document';
import { useCurrentDocumentKey } from '../../composables/use-current-document-key';
import { Summary } from '../../schemas';

export type DocumentSumarizeContentProps = {
  visible?: boolean;
};

const props = defineProps<DocumentSumarizeContentProps>();

const emit = defineEmits(['close']);

const handleClose = () => emit('close');

const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { documentKey } = useCurrentDocumentKey();

const summaries = ref<Array<Summary>>([]);
const isLoading = ref(false);
const isEmpty = computed(() => summaries.value.length === 0);

const useLoadingAtLeast = (loadingRef: Ref<boolean>, ms:number) => {
  let lastTime: number;

  const start = () => {
    lastTime = Date.now();
    // eslint-disable-next-line no-param-reassign
    loadingRef.value = true;
  };

  const finish = () => {
    const deltaTime = Date.now() - lastTime;

    if (deltaTime < ms) {
      setTimeout(() => { isLoading.value = false; }, ms - deltaTime);
    } else {
      isLoading.value = false;
    }
  };

  return { start, finish };
};

const loadingControl = useLoadingAtLeast(isLoading, 2000);
const { t } = useI18n();
const toast = useToast();

const getDocumentSummaries = async () => {
  loadingControl.start();

  try {
    summaries.value = await fetchDocumentSummaries(signatureRequestKey.value, documentKey.value);
  } catch {
    toast.error(t('error.unexpected'));
  } finally {
    loadingControl.finish();
  }
};

const handleHighlightText = (summary: Summary) => {
  // TODO: Do highlight pdf logic
  if (summary) return summary;
};

const thumbsMap = ref(new Map<Summary, string>());

const enum Thumbs {
  UP = 'up',
  DOWN = 'down',
}

const handleThumbChanges = (summary: Summary, thumb: Thumbs) => {
  if (thumbsMap.value.get(summary) === thumb) {
    thumbsMap.value.delete(summary);

    return;
  }

  thumbsMap.value.set(summary, thumb);
};
</script>

<template>
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div
    v-show="props.visible"
    :class="$style.summarizeWrapper"
  >
    <div :class="$style.sumarizeContent">
      <header :class="$style.aiHeader">
        <span :class="$style.forceColors">
          <XIcon
            icon="sparkles"
            size="lg"
            :class="$style.icon"
            left
          />
          <span :class="$style.title"> {{ 'Clickassistant' }} </span>
        </span>

        <XBadge
          text-color="var(--color-neutral-0)"
          color="var(--color-feedback-info-400)"
          small
          :class="$style.betaTag"
        >
          {{ $t('common.feature.beta') }}
          <XIcon icon="gem" />
        </XBadge>

        <div style="flex-grow: 1" />

        <XButton
          design="link"
          model="icon"
          data-testid="closeSummarizeContent"
          @click="handleClose"
        >
          <XIcon
            icon="close"
            icon-style="fak"
          />
        </XButton>
      </header>

      <section
        v-if="isLoading"
        :class="$style.loadingContent"
      >
        <XSkeletonLoader
          height="16px"
          width="40%"
          rounded
        />
        <XSkeletonLoader
          height="16px"
          width="75%"
          rounded
        />
        <XSkeletonLoader
          height="16px"
          width="40%"
          rounded
        />
        <XSkeletonLoader
          height="16px"
          width="75%"
          rounded
        />
        <XSkeletonLoader
          height="16px"
          width="40%"
          rounded
        />
        <XSkeletonLoader
          height="16px"
          width="75%"
          rounded
        />
      </section>

      <section
        v-else-if="isEmpty"
        :class="$style.emptyContent"
      >
        <p>{{ 'Boas-vindas ao Clickassistant, a IA da Clicksign!' }}</p>
        <XButton
          model="plain"
          data-testid="getDocumentInfoButton"
          :class="$style.getDocumentInfoButton"
          @click="getDocumentSummaries"
        >
          {{ 'Listar as principais informações do documento' }}

          <XIcon
            icon="chevron-right"
            right
            :class="$style.infoIcon"
          />
        </XButton>
        <p :class="$style.subtitle">
          {{ 'Mais funcionalidades em breve' }}
        </p>
      </section>

      <section
        v-else
        :class="$style.summaries"
      >
        <p :class="$style.summariesDescription">
          {{ 'Aqui estão as principais informações do documento:' }}
        </p>
        <div
          v-for="item in summaries"
          :key="item.category"
          :class="$style.summaryItem"
          @click="() => handleHighlightText(item)"
        >
          <div :class="$style.summaryTitle">
            <span> {{ item.category }} </span>
            <XIcon
              icon="file-search"
              right
            />
          </div>
          <div> {{ item.summary }} </div>

          <div :class="$style.summaryFloatingActions">
            <XButton
              data-testid="summaryThumbsUp"
              model="plain"
              @click.stop="() => handleThumbChanges(item, Thumbs.UP)"
            >
              <XIcon
                icon="thumbs-up"
                :icon-style="thumbsMap.get(item) === Thumbs.UP ? 'fas' : 'fal'"
              />
            </XButton>

            <XButton
              data-testid="summaryThumbsDown"
              model="plain"
              @click="() => handleThumbChanges(item, Thumbs.DOWN)"
            >
              <XIcon
                icon="thumbs-down"
                :icon-style="thumbsMap.get(item) === Thumbs.DOWN ? 'fas' : 'fal'"
              />
            </XButton>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" module>

.sumarizeContent {
  overflow: auto;
  width: 100vw;
  max-width: 456px;
  min-height: 100%;
  max-height: calc(100vh - 48px - 64px - 4px);
  padding: var(--space-regular);
  color: var(--color-brand-support-01-600);
  background-color: var(--color-neutral-0);
  border-radius: inherit;

  .summaries {
    display: flex;
    flex-direction: column;
    gap: var(--space-regular);
  }

  .summariesDescription {
    margin: 0;
    text-align: center;
  }

  .summariesDescription, .summaryItem {
    color: var(--color-neutral-1000);
  }

  .summaryItem {
    position: relative;
    padding: var(--space-regular);
    background: var(--color-brand-support-03-100);
    border: 1px solid var(--color-neutral-600);
    border-radius: var(--border-radius-large) var(--border-radius-large) var(--border-radius-large) 0;

    // cursor: pointer;

    .summaryTitle {
      margin-bottom: var(--space-small-xx);
      font-weight: var(--font-weight-medium);
    }

    .summaryFloatingActions {
      position: absolute;
      top: var(--space-regular);
      right: var(--space-small-xx);
      display: none;
    }

    &:hover .summaryFloatingActions {
      display: block;
    }
  }

  .loadingContent {
    display: flex;
    flex-direction: column;
    gap: var(--space-regular);
  }

  .emptyContent {
    display: block;
    text-align: center;
    color: var(--color-neutral-1000);

    .subtitle {
      color: var(--color-neutral-700);
    }
  }

  .getDocumentInfoButton {
    margin-bottom: var(--space-regular);
    padding: var(--space-regular);
    font-weight: var(--font-weight-medium);
    border: 1px solid var(--color-brand-primary-200);
    border-radius: var(--border-radius-large);

    .infoIcon {
      color: var(--color-brand-primary-200);
    }
  }

  .aiHeader {
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: var(--space-regular);

    .title {
      font-size: var(--font-size-medium);
    }
  }

  .forceColors {
    position: relative;
    color: black;
    background: white;

    &::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      content: '';
      background: linear-gradient(3deg, #F25924 28.05%, #C100FF 70.09%);
      mix-blend-mode: lighten;
      pointer-events: none;
    }
  }

  .betaTag {
    margin-left: var(--space-small-xx);
    color: var(--color-neutral-0);
  }
}

.summarizeWrapper {
  --border-width: 2px;

  padding: var(--border-width);
  background: linear-gradient(3deg, #F25924 28.05%, #C100FF 70.09%);
  border-radius: var(--border-radius-large) 0 0 var(--border-radius-large);
}
</style>
